// Phase B — Customer Portal shell.
//
// Slim, branded, customer-facing layout. Used INSTEAD of the full forwarder
// Layout when `user.tenant_type === 'customer'`. Auto-routing happens in
// `ProtectedRoute` (the customer never sees /incidents, /tenants, /chain,
// /admin/* — only /portal/*).
import React, { useEffect } from "react";
import { Outlet, useLocation, NavLink } from "react-router-dom";
import { LogOut, ListChecks, FilePlus, Sparkles, Building2, ShieldAlert, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import { LanguageSelector } from "./LanguageSelector";
import { trackModuleView } from "../lib/activityTracker";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const NavBtn = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) => "flex items-center gap-2 px-3 py-2 rounded-md transition-colors"}
    style={({ isActive }) => ({
      background: isActive ? "rgba(155,89,182,0.15)" : "transparent",
      border: `1px solid ${isActive ? "rgba(155,89,182,0.45)" : "rgba(255,255,255,0.08)"}`,
      color: isActive ? "#e0bff0" : "var(--text-dim, #c5cad8)",
      ...mono(11, undefined, 700, "0.6px"),
      textTransform: "uppercase",
      textDecoration: "none",
    })}
    data-testid={testId}
  >
    <Icon size={14} />
    {label}
  </NavLink>
);

export const PortalLayout = () => {
  const { user, logout } = useAuth();
  const { t } = useT();
  const location = useLocation();
  const [forwarderName, setForwarderName] = React.useState(null);
  useEffect(() => { trackModuleView(location.pathname); }, [location.pathname]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const axios = (await import("axios")).default;
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/customer-relationships`);
        if (alive) {
          const fwd = (r.data?.items || []).find((it) => it.relation_type === "customer");
          if (fwd) setForwarderName(fwd.forwarder_name);
        }
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="App min-h-screen" data-testid="customer-portal-root" style={{ background: "#08090d" }}>
      <header
        className="fixed top-0 left-0 right-0 z-[300] flex items-center justify-between px-4 sm:px-6"
        style={{
          height: 56,
          background: "linear-gradient(90deg, #0d0f18 0%, #16121f 100%)",
          borderBottom: "1px solid rgba(155,89,182,0.30)",
        }}
        data-testid="customer-portal-topbar"
      >
        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-2.5 py-1 rounded-md"
            style={{ background: "rgba(155,89,182,0.18)", border: "1px solid rgba(155,89,182,0.45)" }}
          >
            <Building2 size={14} color="#c89adb" />
            <span style={{ ...mono(10, "#e0bff0", 700, "1.4px"), textTransform: "uppercase" }}>
              {t("portal.shell.customer_portal")}
            </span>
          </div>
          {forwarderName && (
            <div style={mono(10, "var(--text-dim, #9aa1b3)", 400, "0.6px")} data-testid="portal-forwarder-brand">
              {t("portal.shell.powered_by")} <span style={{ color: "#f0f2f8", fontWeight: 600 }}>{forwarderName}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <LanguageSelector hasUser={true} testIdPrefix="portal-language" />
          <div
            style={{ ...mono(10, "var(--text-dim, #cfd5e3)") }}
            data-testid="portal-user-email"
          >
            {user?.email}
          </div>
          <button
            onClick={async () => { await logout(); window.location.href = "/login"; }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#ff8a8a",
              cursor: "pointer",
              ...mono(10, "#ff8a8a", 700, "0.8px"),
              textTransform: "uppercase",
            }}
            data-testid="portal-logout-btn"
          >
            <LogOut size={12} /> {t("portal.shell.sign_out")}
          </button>
        </div>
      </header>
      <main className="relative" style={{ paddingTop: 56 }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <nav className="flex items-center gap-2 mb-5 flex-wrap" data-testid="portal-nav">
            <NavBtn to="/portal/my-requests" icon={ListChecks} label={t("portal.nav.my_requests")} testId="portal-nav-my-requests" />
            <NavBtn to="/portal/new" icon={FilePlus} label={t("portal.nav.new_request")} testId="portal-nav-new" />
            <NavBtn to="/portal/chat" icon={Sparkles} label={t("portal.nav.chat")} testId="portal-nav-chat" />
            <NavBtn to="/portal/claims" icon={ShieldAlert} label={t("portal.nav.claims")} testId="portal-nav-claims" />
            <NavBtn to="/portal/approvals" icon={ShieldCheck} label={t("portal.nav.approvals")} testId="portal-nav-approvals" />
          </nav>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default PortalLayout;
